import { useState } from "react";
import { Link2, Link2Off, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

// Section B (Oct 2 list): shows / edits the application a requisition is linked to.
export const AppLinkCell = ({ req, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const link = async () => {
    const sf = window.prompt("Link this bank connection to which application? (e.g. SF-36410)", req.sf_number || "");
    if (!sf) return;
    setBusy(true);
    try {
      await api.post(`/open-banking/requisitions/${req.requisition_id}/link`, { sf_number: sf.trim() });
      toast.success(`Linked to ${sf.trim().toUpperCase()}`);
      onChanged?.();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Could not link"));
    } finally {
      setBusy(false);
    }
  };
  const unlink = async () => {
    setBusy(true);
    try {
      await api.delete(`/open-banking/requisitions/${req.requisition_id}/link`);
      toast.success("Link removed");
      onChanged?.();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Could not unlink"));
    } finally {
      setBusy(false);
    }
  };
  const tone = req.match_basis === "manual" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : req.match_confidence === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className="flex items-center gap-1" data-testid={`ob-app-link-${req.requisition_id}`}>
      {req.sf_number ? (
        <Badge variant="outline" className={`${tone} font-mono text-[11px]`} title={`${req.matched_company || ""} · ${req.match_basis}${req.match_confidence === "medium" ? " (several apps share this name — check)" : ""}`}>
          {req.sf_number}
        </Badge>
      ) : (
        <span className="text-xs text-muted-foreground">not linked</span>
      )}
      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={link} disabled={busy} title="Link to an application" data-testid={`ob-app-link-edit-${req.requisition_id}`}>
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      {req.sf_number && (
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={unlink} disabled={busy} title="Remove link" data-testid={`ob-app-unlink-${req.requisition_id}`}>
          <Link2Off className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
};

export const MatchAllButton = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/open-banking/requisitions/backfill-matches?dry_run=false");
      toast.success(`Matched ${data.matched} of ${data.scanned} unlinked connection(s)${data.unmatched?.length ? ` · ${data.unmatched.length} still unmatched` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(errorDetailMessage(e, "Match run failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={run} disabled={busy} data-testid="ob-match-all-btn" title="Link every unlinked connection to its application by SF number / business name">
      {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
      Match to applications
    </Button>
  );
};
